import { useState } from "react";

/**
 * Provides a clipboard copy utility with a brief visual-confirmation window.
 *
 * `copy(value, key)` — writes `value` to the clipboard and sets `copiedKey`
 * to `key` for 1500 ms, then resets to null (unless a different key was
 * copied in the meantime). Guards on empty value and missing `navigator.clipboard`.
 *
 * `reset()` — immediately clears `copiedKey` (useful when the view context
 * changes, e.g. switching to a different reservation in the sidebar).
 *
 * Usage:
 *   const { copiedKey, copy, reset } = useCopyToClipboard();
 *   // In JSX: copiedKey === "phone" ? <Check /> : <Copy />
 */
export function useCopyToClipboard() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  function copy(value: string, key: string) {
    if (!value || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((cur) => (cur === key ? null : cur)), 1500);
      })
      .catch(() => {});
  }

  function reset() {
    setCopiedKey(null);
  }

  return { copiedKey, copy, reset };
}
